import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { mapi } from './api';
import {
  Button, ButtonRow, Chip, Empty, Field, Grid2, Hint, LinkButton, Muted, NeutralChip, Panel, Warn,
} from '../ui/primitives';
import { Modal } from '../ui/Modal';
import { BillingInfoCard } from './BillingInfoCard';
import { AiCard } from './AiCard';
import { SetupCard } from './SetupCard';
import { UsersCard } from './UsersCard';
import { SearchesCard } from './SearchesCard';
import { Osio } from './Osio';
import { fmtDate, raha, todayStr } from '../lib/format';
import type { AiStatus, CustomerRow, FeatureMeta, Features, Provision } from './types';

const Kortti = styled(Panel)<{ $passiivinen: boolean }>`
  margin-bottom: 14px;
  opacity: ${p => (p.$passiivinen ? 0.65 : 1)};
`;

const Ylarivi = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
  h3 { margin: 0; font-size: 17px; }
`;

const Mittarit = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 10px;
  margin: 14px 0 0;
  .mittari {
    background: ${p => p.theme.color.chipBg};
    border-radius: 8px;
    padding: 8px 10px;
    .arvo { font-size: 18px; font-weight: 700; }
    .nimi { font-size: 12px; color: ${p => p.theme.color.muted}; }
  }
`;

const TilaValikko = styled(Osio)`
  details summary {
    cursor: pointer;
    font-size: 15px;
    font-weight: 700;
    color: ${p => p.theme.color.ink};
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    list-style: none;
  }
  details summary::-webkit-details-marker { display: none; }
  details summary::before {
    content: '▸';
    color: ${p => p.theme.color.muted};
    font-size: 11px;
  }
  details[open] summary::before { content: '▾'; }
`;

const Liput = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 2px 0 8px;
`;

const Siru = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid ${p => p.theme.color.line};
  background: ${p => p.theme.color.card};
  color: ${p => p.theme.color.ink};
  border-radius: 999px;
  padding: 5px 12px;
  cursor: pointer;
  &:hover { background: ${p => p.theme.color.hover}; }
  &[aria-pressed='true'] {
    background: ${p => p.theme.color.ink};
    border-color: ${p => p.theme.color.ink};
    color: ${p => p.theme.color.card};
    &:hover { background: ${p => p.theme.color.ink}; }
  }
`;

const AI_LIPUT = new Set(['feature_ai', 'feature_ai_haku']);

const TYHJA_ASIAKAS = {
  slug: '', name: '', contact_name: '', contact_email: '', website: '',
  create_db: true, user_name: '', user_password: '',
  plan: '', billing_multiplier: '3', billing_currency: 'EUR', usd_to_eur: '0.92',
  monthly_fee: '0', notes: '',
};

const VAIHEET: { key: keyof Provision['steps']; nimi: string }[] = [
  { key: 'database', nimi: 'Tietokanta' },
  { key: 'dns', nimi: 'Osoite' },
  { key: 'app', nimi: 'Sovellus serverillä' },
  { key: 'master', nimi: 'Master käynnistetään uudelleen' },
  { key: 'health', nimi: 'Sovellus vastaa' },
];

function vaiheVari(status: string) {
  if (status === 'ok') return '#5C6B54';
  if (status === 'error') return '#A63D24';
  return '#B85A0C';
}

function overallNimi(overall: string, health?: string) {
  if (overall === 'ok') return 'Käyttövalmis';
  if (overall === 'pending') return 'Käyttöönotto kesken';
  if (health === 'error') return 'Sovellus ei vastaa';
  return 'Häiriö';
}

function ProvisionRivi({
  asiakas, onToast, onPaivita,
}: {
  asiakas: CustomerRow;
  onToast: (v: string) => void;
  onPaivita: () => void;
}) {
  const p = asiakas.provision;
  if (!p) return null;
  const uudelleen = async () => {
    try {
      await mapi(`/api/master/customers/${asiakas.id}/provision`, 'POST');
      onToast('Käyttöönottoa yritetään uudelleen');
      onPaivita();
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };
  const masterTila = p.steps.master?.status;
  const auki = p.overall !== 'ok' || !!p.running;
  const saaYrittaa = !!(p.running || p.overall === 'pending'
    || p.steps.app?.status === 'error'
    || p.steps.master?.status === 'error'
    || p.steps.dns?.status === 'error');
  let ohje: string | null = null;
  if (p.running || p.overall === 'pending') {
    if (masterTila === 'pending') {
      ohje = 'Täpät päivittyvät itsestään — älä lataa sivua uudelleen kesken. '
        + 'Kun master käynnistetään, master.myyntihaku.fi katkeaa hetkeksi. '
        + 'Lataa se vasta kun tämä täppä on vihreä, ja kirjaudu.';
    } else {
      ohje = 'Täpät päivittyvät itsestään. Sivua ei tarvitse ladata uudelleen.';
    }
  } else if (p.overall === 'error') {
    ohje = saaYrittaa
      ? 'Paina Yritä uudelleen. Päivitä vain jos täpät eivät muutu.'
      : 'Paina Päivitä, jos haluat tarkistaa uudelleen vastaako sovellus.';
  }
  const [valikkoAuki, setValikkoAuki] = useState(auki);
  useEffect(() => { if (auki) setValikkoAuki(true); }, [auki]);
  return (
    <TilaValikko>
      <details open={valikkoAuki} onToggle={e => setValikkoAuki((e.target as HTMLDetailsElement).open)}>
        <summary>
          Sovelluksen tila
          <Chip $bg={vaiheVari(p.overall === 'ok' ? 'ok' : p.overall === 'pending' ? 'pending' : 'error')}>
            {overallNimi(p.overall, p.steps.health?.status)}
          </Chip>
        </summary>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginTop: 8 }}>
          {VAIHEET.map(v => {
            const s = p.steps[v.key] || { status: 'pending' };
            return (
              <Chip key={v.key} $bg={vaiheVari(s.status)} title={s.error || v.nimi}>
                {s.status === 'ok' ? '✓' : s.status === 'error' ? '✕' : '…'} {v.nimi}
              </Chip>
            );
          })}
          {asiakas.app_url && (
            <a href={asiakas.app_url} target="_blank" rel="noreferrer"
               style={{ fontSize: 13.5 }}>
              {asiakas.app_url.replace(/^https:\/\//, '')}
            </a>
          )}
          {saaYrittaa && (
            <Button $small onClick={() => void uudelleen()}>Yritä uudelleen</Button>
          )}
        </div>
        {ohje && (
          <Hint style={{ margin: '6px 0 0' }}>{ohje}</Hint>
        )}
        {VAIHEET.filter(v => p.steps[v.key]?.status === 'error' && p.steps[v.key].error).map(v => (
          <Hint key={v.key} style={{ margin: '4px 0 0', color: '#A63D24', whiteSpace: 'pre-wrap' }}>
            {v.nimi}: {p.steps[v.key].error}
          </Hint>
        ))}
      </details>
    </TilaValikko>
  );
}

function SopimusRivi({ asiakas, onToast, onPaivita }: {
  asiakas: CustomerRow;
  onToast: (v: string) => void;
  onPaivita: () => void;
}) {
  const [nimi, setNimi] = useState(asiakas.contact_name || '');
  const [kesken, setKesken] = useState(false);
  const merkitse = async () => {
    if (!nimi.trim()) { onToast('Kirjoita allekirjoittajan nimi'); return; }
    setKesken(true);
    try {
      await mapi(`/api/master/customers/${asiakas.id}/contract`, 'POST', { signed_by: nimi.trim() });
      onToast('Sopimus merkitty allekirjoitetuksi');
      onPaivita();
    } catch (err) {
      onToast(err instanceof Error ? err.message : String(err));
    }
    setKesken(false);
  };
  return (
    <Osio>
      <h4>Sopimus</h4>
      <ButtonRow style={{ margin: '4px 0 0' }}>
        <LinkButton $small href={`/api/master/customers/${asiakas.id}/sopimus`} target="_blank" rel="noreferrer">
          Tulosta sopimus
        </LinkButton>
        {asiakas.contract_signed_at ? (
          <Hint style={{ margin: 0 }}>
            Allekirjoitettu {fmtDate(asiakas.contract_signed_at)}
            {asiakas.contract_signed_by ? ` · ${asiakas.contract_signed_by}` : ''}
            {asiakas.contract_id ? ` · ${asiakas.contract_id}` : ''}
            {asiakas.contract_version ? ` · v${asiakas.contract_version}` : ''}
          </Hint>
        ) : (
          <>
            <input
              style={{ width: 180 }}
              placeholder="Allekirjoittajan nimi"
              value={nimi}
              onChange={e => setNimi(e.target.value)}
            />
            <Button $small $variant="primary" disabled={kesken} onClick={() => void merkitse()}>
              Merkitse allekirjoitetuksi
            </Button>
          </>
        )}
      </ButtonRow>
    </Osio>
  );
}

export function CustomersView({ onToast }: { onToast: (v: string) => void }) {
  const [rivit, setRivit] = useState<CustomerRow[] | null>(null);
  const [meta, setMeta] = useState<FeatureMeta[]>([]);
  const [laskutusKentat, setLaskutusKentat] = useState<FeatureMeta[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lomake, setLomake] = useState<typeof TYHJA_ASIAKAS | null>(null);
  const [muokattava, setMuokattava] = useState<CustomerRow | null>(null);
  const [poistettava, setPoistettava] = useState<CustomerRow | null>(null);
  const [poistoNimi, setPoistoNimi] = useState('');
  const [poistoKaynnissa, setPoistoKaynnissa] = useState(false);

  const lataa = useCallback(async () => {
    try {
      const [c, f] = await Promise.all([
        mapi<{ customers: CustomerRow[] }>('/api/master/customers'),
        mapi<{ features: FeatureMeta[]; billingFields: FeatureMeta[] }>('/api/master/features'),
      ]);
      setRivit(c.customers);
      setMeta(f.features);
      setLaskutusKentat(f.billingFields);
      setVirhe(null);
    } catch (err) { setVirhe(err instanceof Error ? err.message : String(err)); }
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  useEffect(() => {
    const kesken = (rivit || []).some(c =>
      c.provision && (c.provision.overall === 'pending' || c.provision.running));
    if (!kesken) return;
    const t = setInterval(() => { void lataa(); }, 3000);
    return () => clearInterval(t);
  }, [rivit, lataa]);

  const [vahvistus, setVahvistus] = useState<{
    asiakas: CustomerRow; key: string; label: string; hint: string; paalle: boolean;
  } | null>(null);
  const [maksuton, setMaksuton] = useState<{ asiakas: CustomerRow } | null>(null);
  const [maksutonKaynnissa, setMaksutonKaynnissa] = useState(false);
  const [maksutonTapa, setMaksutonTapa] = useState<'toistaiseksi' | 'asti'>('toistaiseksi');
  const [maksutonPaiva, setMaksutonPaiva] = useState('');
  const [avain, setAvain] = useState('');
  const [vahvistaa, setVahvistaa] = useState(false);

  const tarvitseeAvaimen = !!vahvistus
    && vahvistus.paalle
    && AI_LIPUT.has(vahvistus.key)
    && !vahvistus.asiakas.ai.configured;

  const vahvistaLippu = async () => {
    if (!vahvistus) return;
    if (tarvitseeAvaimen && !avain.trim()) return;
    const { asiakas: c, key, label, paalle } = vahvistus;
    setVahvistaa(true);
    try {
      let ai = c.ai;
      if (tarvitseeAvaimen) {
        const t = await mapi<{ ai: AiStatus }>(`/api/master/customers/${c.id}/ai`, 'PUT',
          { key: avain.trim() });
        ai = t.ai;
      }
      const uudet: Features = { ...c.features, [key]: paalle };
      const r = await mapi<{ features: Features; synced: boolean; error: string | null }>(
        `/api/master/customers/${c.id}/features`, 'PUT', { features: uudet });
      setRivit(edelliset => (edelliset || []).map(x => (
        x.id === c.id ? { ...x, ai, features: r.features } : x
      )));
      onToast(r.synced
        ? `${c.name}: ${label} ${paalle ? 'käyttöön' : 'pois'}`
        : `Tallennettu masteriin, mutta asiakkaan kantaan ei päästy: ${r.error}`);
      setVahvistus(null);
      setAvain('');
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setVahvistaa(false);
  };

  const avaaMaksuton = (c: CustomerRow) => {
    const asti = String(c.ai_free_until || '').slice(0, 10);
    setMaksutonTapa(asti ? 'asti' : 'toistaiseksi');
    setMaksutonPaiva(asti);
    setMaksuton({ asiakas: c });
  };

  const vahvistaMaksuton = async (paalle: boolean) => {
    if (!maksuton) return;
    if (paalle && maksutonTapa === 'asti' && !maksutonPaiva) return;
    const c = maksuton.asiakas;
    setMaksutonKaynnissa(true);
    try {
      await mapi(`/api/master/customers/${c.id}`, 'PATCH', {
        ai_free: paalle ? 1 : 0,
        ai_free_until: paalle && maksutonTapa === 'asti' ? maksutonPaiva : '',
      });
      setMaksuton(null);
      await lataa();
      onToast(paalle
        ? (maksutonTapa === 'asti'
          ? `${c.name}: tekoäly maksutta ${fmtDate(maksutonPaiva)} asti`
          : `${c.name}: tekoäly on maksuton toistaiseksi`)
        : `${c.name}: tekoäly laskutetaan taas`);
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    setMaksutonKaynnissa(false);
  };

  const tallennaUusi = async () => {
    if (!lomake) return;
    try {
      const r = await mapi<{ id: number; warning?: string }>(
        '/api/master/customers', 'POST', {
          ...lomake,
          billing_multiplier: Number(lomake.billing_multiplier),
          usd_to_eur: Number(lomake.usd_to_eur),
          monthly_fee: Number(lomake.monthly_fee),
        });
      setLomake(null);
      await lataa();
      onToast(r.warning || 'Asiakas lisätty');
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };

  const tallennaMuutos = async () => {
    if (!muokattava) return;
    try {
      await mapi(`/api/master/customers/${muokattava.id}`, 'PATCH', {
        name: muokattava.name,
        contact_name: muokattava.contact_name,
        contact_email: muokattava.contact_email,
        db_path: muokattava.db_path,
        plan: muokattava.plan,
        active: muokattava.active,
        billing_multiplier: Number(muokattava.billing_multiplier),
        billing_currency: muokattava.billing_currency,
        usd_to_eur: Number(muokattava.usd_to_eur),
        monthly_fee: Number(muokattava.monthly_fee),
        payment_terms: Number(muokattava.payment_terms),
        invoice_period: muokattava.invoice_period,
        our_reference: muokattava.our_reference,
        contract_id: muokattava.contract_id,
        notes: muokattava.notes,
      });
      setMuokattava(null);
      await lataa();
      onToast('Tallennettu');
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
  };

  const poista = async () => {
    if (!poistettava) return;
    if (poistoNimi.trim() !== poistettava.name.trim()) return;
    setPoistoKaynnissa(true);
    try {
      const r = await mapi<{ warning?: string }>(
        `/api/master/customers/${poistettava.id}`, 'DELETE', { nimi: poistoNimi.trim() });
      setPoistettava(null);
      setPoistoNimi('');
      await lataa();
      onToast(r.warning || 'Asiakas, sovellus ja osoite poistettu');
    } catch (err) { onToast(err instanceof Error ? err.message : String(err)); }
    finally { setPoistoKaynnissa(false); }
  };

  if (virhe) return <Warn>{virhe}</Warn>;
  if (!rivit) return <Empty>Ladataan…</Empty>;

  return (
    <>
      <ButtonRow>
        <Button $variant="primary" onClick={() => setLomake({ ...TYHJA_ASIAKAS })}>
          + Lisää asiakas
        </Button>
        <Button onClick={lataa} title="Täpät päivittyvät käyttöönoton aikana itsestään. Tätä tarvitaan vain jos näkymä jää vanhaksi.">
          ↻ Päivitä
        </Button>
        <span style={{ flex: 1 }} />
        <Muted>{rivit.length} asiakasta</Muted>
      </ButtonRow>

      {rivit.length === 0 && (
        <Empty>
          Ei asiakkaita vielä. Lisää ensimmäinen — osoite, tietokanta ja sovellus
          syntyvät automaattisesti.
        </Empty>
      )}

      {rivit.map(c => (
        <Kortti key={c.id} $passiivinen={!c.active}>
          <Ylarivi>
            <h3>{c.name}</h3>
            <NeutralChip>{c.slug}</NeutralChip>
            {c.plan && <Chip $bg="#262626">{c.plan}</Chip>}
            {!c.active && <Chip $bg="#6B6B64">Passiivinen</Chip>}
            {c.stats.saatavilla && !c.profile.valmis && (
              <Chip $bg="#A63D24" title={'Puuttuu: ' + c.profile.puuttuu.join(', ')}>
                Yritysprofiili kesken
              </Chip>
            )}
            <span style={{ flex: 1 }} />
            <Button $small onClick={() => setMuokattava(c)}>Muokkaa</Button>
            <Button $small $variant="danger" onClick={() => { setPoistettava(c); setPoistoNimi(''); }}>
              Poista
            </Button>
          </Ylarivi>

          {c.contact_email && <Hint style={{ margin: '4px 0 0' }}>
            {c.contact_name ? `${c.contact_name} · ` : ''}{c.contact_email}
          </Hint>}

          <SopimusRivi asiakas={c} onToast={onToast} onPaivita={() => void lataa()} />

          <ProvisionRivi asiakas={c} onToast={onToast} onPaivita={() => void lataa()} />

          <Mittarit>
            <div className="mittari">
              <div className="arvo">{c.stats.leads ?? '—'}</div><div className="nimi">Liidiä</div>
            </div>
            <div className="mittari">
              <div className="arvo">{c.stats.users ?? '—'}</div><div className="nimi">Käyttäjää</div>
            </div>
            <div className="mittari">
              <div className="arvo">{c.stats.sent ?? '—'}</div><div className="nimi">Viestiä lähetetty</div>
            </div>
            <div className="mittari">
              <div className="arvo">{c.billing.calls}</div><div className="nimi">AI-toimintoa kk</div>
            </div>
            {c.billing.ai_free && (
              <>
                <div className="mittari">
                  <div className="arvo">{raha(c.billing.usage_price, c.billing.currency)}</div>
                  <div className="nimi">AI-kulutus</div>
                </div>
                <div className="mittari">
                  <div className="arvo">{raha(c.billing.unbilled, c.billing.currency)}</div>
                  <div className="nimi">Ei laskutettu</div>
                </div>
              </>
            )}
            <div className="mittari">
              <div className="arvo">{raha(c.billing.amount, c.billing.currency)}</div>
              <div className="nimi">Laskutetaan kk</div>
            </div>
            <div className="mittari">
              <div className="arvo">{raha(c.billing.cost, c.billing.currency)}</div>
              <div className="nimi">Kulu (sisäinen)</div>
            </div>
          </Mittarit>

          <Osio>
            <h4>Ominaisuudet</h4>
            <Liput>
              {meta.map(f => {
                const paalla = c.features[f.key] !== false;
                return (
                  <Siru
                    key={f.key}
                    type="button"
                    title={f.hint}
                    aria-pressed={paalla}
                    onClick={() => {
                      setAvain('');
                      setVahvistus({
                        asiakas: c, key: f.key, label: f.label, hint: f.hint, paalle: !paalla,
                      });
                    }}
                  >
                    {paalla && <span aria-hidden="true">✓</span>}
                    {f.label}
                  </Siru>
                );
              })}
              <Siru
                type="button"
                title="Asiakasta ei veloiteta tekoälyn käytöstä. Voit asettaa ajan toistaiseksi tai tiettyyn päivään asti."
                aria-pressed={!!c.billing.ai_free_nyt}
                onClick={() => avaaMaksuton(c)}
              >
                {c.billing.ai_free_nyt && <span aria-hidden="true">✓</span>}
                {Number(c.ai_free) === 1
                  ? (c.billing.ai_free_nyt
                    ? (c.ai_free_until
                      ? `AI maksutta ${fmtDate(c.ai_free_until)} asti`
                      : 'AI maksutta toistaiseksi')
                    : `AI maksutta päättyi${c.ai_free_until ? ` ${fmtDate(c.ai_free_until)}` : ''}`)
                  : 'AI maksutta'}
              </Siru>
            </Liput>
            <Hint style={{ margin: 0 }}>
              Muutos kirjoitetaan heti asiakkaan omaan tietokantaan. Asiakkaan sovellus
              piilottaa toiminnon ja palvelin estää sen käytön.
              {c.stats.viimeisin && ` · Viimeisin tapahtuma ${fmtDate(c.stats.viimeisin)}`}
            </Hint>
            {(c.features.feature_ai !== false || c.features.feature_ai_haku !== false) && !c.ai.configured && (
              <Warn style={{ marginTop: 8 }}>
                {c.features.feature_ai_haku !== false && c.features.feature_ai !== false
                  ? 'AI-viestiluonnokset ja AI-liidihaku on valittu, mutta API-avainta ei ole. Kumpikaan ei toimi ennen avainta.'
                  : c.features.feature_ai_haku !== false
                    ? 'AI-liidihaku on valittu, mutta API-avainta ei ole. Haku ei toimi ennen avainta.'
                    : 'AI-viestiluonnokset on valittu, mutta API-avainta ei ole. Luonnokset eivät toimi ennen avainta.'}
              </Warn>
            )}
          </Osio>

          {c.stats.saatavilla && !c.profile.valmis && (
            <Warn>
              Asiakas ei ole vielä kuvannut liiketoimintaansa (puuttuu:{' '}
              {c.profile.puuttuu.join(', ')}). AI-toiminnot eivät toimi ennen sitä — ohjaa
              asiakkaan ylläpitäjä täyttämään <strong>Yritysprofiili</strong>-välilehti. Tietoja ei peritä
              toiselta asiakkaalta, joten jokainen yritys määrittää omansa.
            </Warn>
          )}

          <AiCard
            asiakas={c}
            onToast={onToast}
            onSaved={ai => setRivit(edelliset =>
              (edelliset || []).map(x => (x.id === c.id ? { ...x, ai } : x)))}
          />

          <UsersCard asiakas={c} onToast={onToast} onChanged={lataa} />

          <SearchesCard asiakas={c} onToast={onToast} />

          <SetupCard asiakas={c} onToast={onToast} onDone={lataa} />

          <BillingInfoCard
            asiakas={c}
            kentat={laskutusKentat}
            onToast={onToast}
            onSaved={info => setRivit(edelliset =>
              (edelliset || []).map(x => (x.id === c.id ? { ...x, billingInfo: info } : x)))}
          />
        </Kortti>
      ))}

      {maksuton && (
        <Modal onClose={() => { if (!maksutonKaynnissa) setMaksuton(null); }} leveys="480px">
          <h2 style={{ marginTop: 0 }}>Tekoäly maksutta</h2>
          <p style={{ margin: '0 0 8px' }}>
            Asiakasta <strong>{maksuton.asiakas.name}</strong> ei veloiteta tekoälyn käytöstä.
            Kiinteä kuukausimaksu laskutetaan edelleen.
          </p>
          <Hint style={{ marginTop: 0 }}>
            Voit pitää tämän toistaiseksi tai päättyvänä tiettynä päivänä.
            Päivän jälkeen tekoäly laskutetaan automaattisesti.
          </Hint>
          <Field>
            <label>
              <input
                type="radio" name="maksuton_tapa" style={{ marginRight: 8 }}
                checked={maksutonTapa === 'toistaiseksi'}
                onChange={() => setMaksutonTapa('toistaiseksi')}
              />
              Toistaiseksi voimassa
            </label>
          </Field>
          <Field>
            <label>
              <input
                type="radio" name="maksuton_tapa" style={{ marginRight: 8 }}
                checked={maksutonTapa === 'asti'}
                onChange={() => setMaksutonTapa('asti')}
              />
              Tiettyyn päivään asti
            </label>
            {maksutonTapa === 'asti' && (
              <input
                type="date" min={todayStr()}
                value={maksutonPaiva}
                onChange={e => setMaksutonPaiva(e.target.value)}
                style={{ marginTop: 8 }}
              />
            )}
          </Field>
          <ButtonRow>
            <Button
              $variant="primary"
              disabled={maksutonKaynnissa || (maksutonTapa === 'asti' && !maksutonPaiva)}
              onClick={() => void vahvistaMaksuton(true)}
            >
              {maksutonKaynnissa ? 'Tallennetaan…' : 'Tallenna'}
            </Button>
            {Number(maksuton.asiakas.ai_free) === 1 && (
              <Button
                $variant="danger"
                disabled={maksutonKaynnissa}
                onClick={() => void vahvistaMaksuton(false)}
              >
                Laskuta taas
              </Button>
            )}
            <Button disabled={maksutonKaynnissa} onClick={() => setMaksuton(null)}>Peruuta</Button>
          </ButtonRow>
        </Modal>
      )}

      {vahvistus && (
        <Modal onClose={() => { if (!vahvistaa) { setVahvistus(null); setAvain(''); } }} leveys="480px">
          <h2 style={{ marginTop: 0 }}>
            {vahvistus.paalle ? 'Laita ominaisuus päälle' : 'Poista ominaisuus käytöstä'}
          </h2>
          <p style={{ margin: '0 0 8px' }}>
            {vahvistus.paalle
              ? <>Laitetaanko <strong>{vahvistus.label}</strong> päälle asiakkaalle {vahvistus.asiakas.name}?</>
              : <>Poistetaanko <strong>{vahvistus.label}</strong> käytöstä asiakkaalta {vahvistus.asiakas.name}?</>}
          </p>
          {vahvistus.hint && <Hint style={{ marginTop: 0 }}>{vahvistus.hint}</Hint>}
          {tarvitseeAvaimen && (
            <Field>
              <label htmlFor="vahvistus_avain">Anthropic API-avain</label>
              <input
                id="vahvistus_avain" type="password" autoComplete="off" autoFocus
                value={avain} onChange={e => setAvain(e.target.value)}
                placeholder="sk-ant-…"
              />
              <Warn>
                Et voi laittaa tätä ominaisuutta päälle, ellet ole lisännyt API-avainta.
              </Warn>
            </Field>
          )}
          <ButtonRow>
            <Button
              $variant="primary"
              disabled={vahvistaa || (tarvitseeAvaimen && !avain.trim())}
              onClick={() => void vahvistaLippu()}
            >
              {vahvistaa ? 'Tallennetaan…' : (vahvistus.paalle ? 'Laita päälle' : 'Poista käytöstä')}
            </Button>
            <Button disabled={vahvistaa} onClick={() => { setVahvistus(null); setAvain(''); }}>
              Peruuta
            </Button>
          </ButtonRow>
        </Modal>
      )}

      {lomake && (
        <Modal onClose={() => setLomake(null)} leveys="640px">
          <h2 style={{ marginTop: 0 }}>Lisää ohjelma-asiakas</h2>
          <Grid2>
            <Field>
              <label>Tunnus (slug) *</label>
              <input
                value={lomake.slug} placeholder="esim. provision-test"
                onChange={e => setLomake({ ...lomake, slug: e.target.value })}
              />
            </Field>
            <Field>
              <label>Nimi *</label>
              <input
                value={lomake.name} placeholder="ISOFAN Oy"
                onChange={e => setLomake({ ...lomake, name: e.target.value })}
              />
            </Field>
          </Grid2>
          <Hint style={{ margin: '0 0 10px' }}>
            Osoitteeksi tulee <code>https://{lomake.slug || 'slug'}.myyntihaku.fi</code>.
            Tietokanta, osoite ja sovellus serverillä luodaan automaattisesti.
          </Hint>
          <Field>
            <label>Yrityksen verkkosivu</label>
            <input
              value={lomake.website} placeholder="https://www.yritys.fi"
              onChange={e => setLomake({ ...lomake, website: e.target.value })}
            />
            <Hint style={{ margin: "2px 0 0" }}>
              AI lukee sivun ja esitäyttää yritysprofiilin, toimialat ja viestipohjat
              — voit tehdä sen heti lisäämisen jälkeen asiakkaan kortilta.
            </Hint>
          </Field>

          <Grid2>
            <Field>
              <label>Ensimmäinen myyjätunnus</label>
              <input
                value={lomake.user_name} placeholder="etunimi"
                onChange={e => setLomake({ ...lomake, user_name: e.target.value })}
              />
            </Field>
            <Field>
              <label>Salasana (väh. 6 merkkiä)</label>
              <input
                value={lomake.user_password}
                onChange={e => setLomake({ ...lomake, user_password: e.target.value })}
              />
            </Field>
          </Grid2>
          <Hint style={{ marginTop: 0 }}>
            Tunnuksesta tulee ylläpitäjä, joka voi lisätä muut käyttäjät itse. Voit
            jättää tyhjäksi ja luoda tunnukset myöhemmin asiakaskortilta.
          </Hint>
          <Grid2>
            <Field>
              <label>Yhteyshenkilö</label>
              <input value={lomake.contact_name}
                     onChange={e => setLomake({ ...lomake, contact_name: e.target.value })} />
            </Field>
            <Field>
              <label>Sähköposti</label>
              <input value={lomake.contact_email}
                     onChange={e => setLomake({ ...lomake, contact_email: e.target.value })} />
            </Field>
            <Field>
              <label>Paketti</label>
              <input value={lomake.plan} placeholder="perus / pro"
                     onChange={e => setLomake({ ...lomake, plan: e.target.value })} />
            </Field>
            <Field>
              <label>Kiinteä kuukausimaksu</label>
              <input type="number" step="0.01" value={lomake.monthly_fee}
                     onChange={e => setLomake({ ...lomake, monthly_fee: e.target.value })} />
            </Field>
            <Field>
              <label>AI-kulun kerroin</label>
              <input type="number" step="0.1" value={lomake.billing_multiplier}
                     onChange={e => setLomake({ ...lomake, billing_multiplier: e.target.value })} />
            </Field>
            <Field>
              <label>Valuutta</label>
              <select value={lomake.billing_currency}
                      onChange={e => setLomake({ ...lomake, billing_currency: e.target.value })}>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </Field>
          </Grid2>
          <Field>
            <label>Muistiinpanot</label>
            <textarea rows={2} value={lomake.notes}
                      onChange={e => setLomake({ ...lomake, notes: e.target.value })} />
          </Field>
          <ButtonRow>
            <Button $variant="primary" onClick={tallennaUusi}>Lisää asiakas</Button>
            <Button onClick={() => setLomake(null)}>Peruuta</Button>
          </ButtonRow>
        </Modal>
      )}

      {muokattava && (
        <Modal onClose={() => setMuokattava(null)} leveys="640px">
          <h2 style={{ marginTop: 0 }}>{muokattava.name}</h2>
          <Grid2>
            <Field>
              <label>Nimi</label>
              <input value={muokattava.name}
                     onChange={e => setMuokattava({ ...muokattava, name: e.target.value })} />
            </Field>
            <Field>
              <label>Paketti</label>
              <input value={muokattava.plan || ''}
                     onChange={e => setMuokattava({ ...muokattava, plan: e.target.value })} />
            </Field>
          </Grid2>
          <Field>
            <label>Polku tietokantaan</label>
            <input value={muokattava.db_path}
                   onChange={e => setMuokattava({ ...muokattava, db_path: e.target.value })} />
          </Field>
          <Grid2>
            <Field>
              <label>Yhteyshenkilö</label>
              <input value={muokattava.contact_name || ''}
                     onChange={e => setMuokattava({ ...muokattava, contact_name: e.target.value })} />
            </Field>
            <Field>
              <label>Sähköposti</label>
              <input value={muokattava.contact_email || ''}
                     onChange={e => setMuokattava({ ...muokattava, contact_email: e.target.value })} />
            </Field>
            <Field>
              <label>Kiinteä kuukausimaksu</label>
              <input type="number" step="0.01" value={muokattava.monthly_fee}
                     onChange={e => setMuokattava({ ...muokattava, monthly_fee: Number(e.target.value) })} />
            </Field>
            <Field>
              <label>AI-kulun kerroin</label>
              <input type="number" step="0.1" value={muokattava.billing_multiplier}
                     onChange={e => setMuokattava({ ...muokattava, billing_multiplier: Number(e.target.value) })} />
            </Field>
            <Field>
              <label>Valuutta</label>
              <select
                value={muokattava.billing_currency}
                onChange={e => setMuokattava({
                  ...muokattava, billing_currency: e.target.value as 'EUR' | 'USD',
                })}
              >
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </Field>
            <Field>
              <label>Tila</label>
              <select
                value={muokattava.active}
                onChange={e => setMuokattava({ ...muokattava, active: Number(e.target.value) })}
              >
                <option value={1}>Aktiivinen</option>
                <option value={0}>Passiivinen</option>
              </select>
            </Field>
            <Field>
              <label>Maksuehto (päivää)</label>
              <input
                type="number" min={0} max={90} value={muokattava.payment_terms}
                onChange={e => setMuokattava({ ...muokattava, payment_terms: Number(e.target.value) })}
              />
            </Field>
            <Field>
              <label>Laskutusjakso</label>
              <select
                value={muokattava.invoice_period}
                onChange={e => setMuokattava({ ...muokattava, invoice_period: e.target.value })}
              >
                <option value="kuukausittain">Kuukausittain</option>
                <option value="neljannesvuosittain">Neljännesvuosittain</option>
                <option value="vuosittain">Vuosittain</option>
              </select>
            </Field>
            <Field>
              <label>Viitteemme</label>
              <input
                value={muokattava.our_reference || ''}
                onChange={e => setMuokattava({ ...muokattava, our_reference: e.target.value })}
              />
            </Field>
            <Field>
              <label>Sopimusnumero</label>
              <input
                value={muokattava.contract_id || ''}
                onChange={e => setMuokattava({ ...muokattava, contract_id: e.target.value })}
              />
            </Field>
          </Grid2>
          <Field>
            <label>Muistiinpanot</label>
            <textarea rows={2} value={muokattava.notes || ''}
                      onChange={e => setMuokattava({ ...muokattava, notes: e.target.value })} />
          </Field>
          <ButtonRow>
            <Button $variant="primary" onClick={tallennaMuutos}>Tallenna</Button>
            <Button onClick={() => setMuokattava(null)}>Peruuta</Button>
          </ButtonRow>
        </Modal>
      )}

      {poistettava && (
        <Modal onClose={() => { if (!poistoKaynnissa) { setPoistettava(null); setPoistoNimi(''); } }} leveys="480px">
          <h2 style={{ marginTop: 0 }}>Poista {poistettava.name}</h2>
          <Warn>
            Tätä ei voi perua. Poisto tekee kaiken tämän:
          </Warn>
          <ul style={{ margin: '8px 0 12px', paddingLeft: 20, fontSize: 14, lineHeight: 1.5 }}>
            <li>irrottaa volume-linkityksen masterista (<code>/app/tenants/{poistettava.slug}/data</code>) ja käynnistää masterin uudelleen</li>
            <li>poistaa Coolify-appin ja sen volumen</li>
            <li>poistaa osoitteen Cloudflarestä ({(poistettava.app_url || `${poistettava.slug}.myyntihaku.fi`).replace(/^https:\/\//, '')})</li>
            <li>poistaa asiakkaan masterin rekisteristä</li>
          </ul>
          <Field>
            <label>Kirjoita asiakkaan nimi vahvistaaksesi: {poistettava.name}</label>
            <input
              value={poistoNimi}
              autoComplete="off"
              disabled={poistoKaynnissa}
              placeholder={poistettava.name}
              onChange={e => setPoistoNimi(e.target.value)}
            />
          </Field>
          <ButtonRow>
            <Button
              $variant="danger"
              disabled={poistoKaynnissa || poistoNimi.trim() !== poistettava.name.trim()}
              onClick={() => void poista()}
            >
              {poistoKaynnissa ? 'Poistetaan…' : 'Poista pysyvästi'}
            </Button>
            <Button disabled={poistoKaynnissa} onClick={() => { setPoistettava(null); setPoistoNimi(''); }}>
              Peruuta
            </Button>
          </ButtonRow>
        </Modal>
      )}
    </>
  );
}
